import { Product } from 'entities/product';
import { SrpPlanPreview } from 'entities/srp-plan-preview';
import { ProductAttributes } from 'entities/product-attributes';
import { shippingMethodCodeForCountry } from './shipping';
import { PRODUCT_SKUS } from 'lib/constants/product-skus';
import { isPurchasableInCountry } from 'lib/constants/country-quantity-limits';

export const SRP_PROMO_CODE = 'stp_domestic_10_percent';
export const SRP_PRODUCT_CODE = 'syn-rf-3mo';
export const SRP_PRODUCT_CODE_6MO = 'syn-rf-6mo';

// srp-enroll resolves which subscription to upgrade — and disambiguates when a
// member has more than one — so callers deep-link here, not to a built path.
export const SRP_ENROLL_PATH = '/account/subscriptions/srp-enroll';
export const SRP_SAVINGS_CURRENCY = 'USD';

// Plan price × SRP discount (10% for 3 month, 15% for 6 month) × deliveries per year.
export const SRP_YEARLY_SAVINGS_USD: Record<string, number> = {
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_3MO]: 60,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL_3MO]: 60,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_6MO]: 90,
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL_6MO]: 90,
  [PRODUCT_SKUS.ENERGY_FOCUS_3MO]: 42,
  [PRODUCT_SKUS.SLEEP_RESTORE_3MO]: 42,
  [PRODUCT_SKUS.DAILY_MULTIVITAMIN_3MO]: 48,
};

// The "save up to" figure the upgrade banner advertises.
export const SRP_SAVINGS_USD = Math.max(
  ...Object.values(SRP_YEARLY_SAVINGS_USD),
);

// The BOGO discount function behind these codes qualifies only the monthly
// plan, so an SRP swap would void the offer.
export const SRP_BLOCKING_PROMO_CODES = ['FREEGIFT', 'FREEGIFTSMS'];

export const isSrpBlockedByPromoCode = (promoCode?: string | null): boolean =>
  !!promoCode && SRP_BLOCKING_PROMO_CODES.includes(promoCode.toUpperCase());

const SRP_TERMS_BY_BASE_SKU: Record<string, readonly string[]> = {
  [PRODUCT_SKUS.DAILY_SYNBIOTIC]: [
    PRODUCT_SKUS.DAILY_SYNBIOTIC_3MO,
    PRODUCT_SKUS.DAILY_SYNBIOTIC_6MO,
  ],
  [PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL]: [
    PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL_3MO,
    PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL_6MO,
  ],
  [PRODUCT_SKUS.ENERGY_FOCUS]: [PRODUCT_SKUS.ENERGY_FOCUS_3MO],
  [PRODUCT_SKUS.SLEEP_RESTORE]: [PRODUCT_SKUS.SLEEP_RESTORE_3MO],
  [PRODUCT_SKUS.DAILY_MULTIVITAMIN]: [PRODUCT_SKUS.DAILY_MULTIVITAMIN_3MO],
};

export const SRP_FAMILY_BASE_SKUS = Object.keys(SRP_TERMS_BY_BASE_SKU);

export const getSrpTermMonths = (sku: string): number | null => {
  const match = sku.match(/-(\d+)mo$/);
  return match ? Number(match[1]) : null;
};

export const COBIOTICS_SRP_BASE_SKUS: string[] = [
  PRODUCT_SKUS.ENERGY_FOCUS,
  PRODUCT_SKUS.SLEEP_RESTORE,
  PRODUCT_SKUS.DAILY_MULTIVITAMIN,
];

// Refill bases only: the enrollment page never offers syn-wk-3mo/6mo.
const getSrpRefillBaseSkus = (isCobioticsSrpEnabled = false): string[] => [
  PRODUCT_SKUS.DAILY_SYNBIOTIC_REFILL,
  ...(isCobioticsSrpEnabled
    ? [
        PRODUCT_SKUS.ENERGY_FOCUS_REFILL,
        PRODUCT_SKUS.DAILY_MULTIVITAMIN_REFILL,
        PRODUCT_SKUS.SLEEP_RESTORE_REFILL,
      ]
    : []),
];

// Every SRP family's upgrade-target term codes; Co-biotics offer the 3 month term only.
export const getSrpProductCodes = (isCobioticsSrpEnabled = false): string[] => [
  SRP_PRODUCT_CODE,
  SRP_PRODUCT_CODE_6MO,
  ...(isCobioticsSrpEnabled
    ? [
        PRODUCT_SKUS.ENERGY_FOCUS_REFILL_3MO,
        PRODUCT_SKUS.DAILY_MULTIVITAMIN_REFILL_3MO,
        PRODUCT_SKUS.SLEEP_RESTORE_REFILL_3MO,
      ]
    : []),
];

// Enroll into their refill family; they have no term products of their own.
const getSrpEligibleWelcomeKitSkus = (
  isCobioticsSrpEnabled = false,
): string[] => (isCobioticsSrpEnabled ? COBIOTICS_SRP_BASE_SKUS : []);

export const getSrpEligiblePlanCodes = (
  isCobioticsSrpEnabled = false,
): string[] => [
  PRODUCT_SKUS.DAILY_SYNBIOTIC,
  ...getSrpRefillBaseSkus(isCobioticsSrpEnabled),
  ...getSrpProductCodes(isCobioticsSrpEnabled),
  ...getSrpEligibleWelcomeKitSkus(isCobioticsSrpEnabled),
];

// Any longer-term AM-02, PM-02 or DM-02 plan (Welcome Kit or Refill) in the
// cart covers its monthly Welcome Kit, so that plan is not pitched as an upsell.
export const getUpsellSkusCoveredBySrp = (cartSkus: string[]): string[] =>
  COBIOTICS_SRP_BASE_SKUS.filter((baseSku) => {
    const productPrefix = baseSku.split('-')[0];
    return cartSkus.some(
      (sku) => sku.startsWith(`${productPrefix}-`) && getSrpTermMonths(sku),
    );
  });

// Every item is SRP or one-time (autoRenew false). Unresolved skus count as
// eligible, so this stays false until products are known.
export const isBundleIneligibleCart = (
  skus: string[],
  getProduct: (sku: string) => Product,
): boolean =>
  skus.length > 0 &&
  skus.every((sku) => {
    const product = getProduct(sku);
    return (
      Boolean(product?.code) &&
      (Boolean(product.isSrp) || product.autoRenew === false)
    );
  });

export const getSrpFamilySkus = (
  planCode: string,
): readonly string[] | null => {
  const family = Object.entries(SRP_TERMS_BY_BASE_SKU).find(
    ([baseSku, termSkus]) =>
      planCode === baseSku || termSkus.includes(planCode),
  );
  if (!family) {
    return null;
  }
  const [baseSku, termSkus] = family;
  return [baseSku, ...termSkus];
};

// The monthly plan plus every longer term the shopper's country sells,
// shortest first. Null when there is no SRP family or nothing to upgrade to.
export const getSrpPlanSkusForCountry = (
  planCode: string,
  countryCode: string,
): string[] | null => {
  const skus = getSrpFamilySkus(planCode);
  if (!skus) {
    return null;
  }
  const [baseSku, ...upgradeSkus] = skus;
  const availableUpgradeSkus = upgradeSkus.filter((sku) =>
    isPurchasableInCountry(sku, countryCode),
  );
  return availableUpgradeSkus.length
    ? [baseSku, ...availableUpgradeSkus]
    : null;
};

export const isSrpCountry = (countryCode: string): boolean =>
  SRP_FAMILY_BASE_SKUS.some(
    (baseSku) => !!getSrpPlanSkusForCountry(baseSku, countryCode),
  );

const getSubscriptionPlanCode = (subscription): string =>
  subscription.planCode || subscription.product_plan?.plan?.plan_code;

// Normalized subscriptions use shippingAddress; raw API ones use shipping_address.
const getSubscriptionCountry = (subscription): string =>
  subscription.shippingAddress?.country ??
  subscription.shipping_address?.country ??
  '';

// SRP terms with an international (-int) Recharge variant; the rest sell in the US only.
const INTERNATIONAL_SRP_PRODUCT_CODES = [
  SRP_PRODUCT_CODE,
  SRP_PRODUCT_CODE_6MO,
];

// The SRP terms a subscription can upgrade to from where it ships.
const getSrpUpgradeCodes = (
  planCode: string,
  countryCode: string,
  isCobioticsSrpEnabled: boolean,
): string[] => {
  const prefix = planCode.split('-')[0].toLowerCase();
  return getSrpProductCodes(isCobioticsSrpEnabled).filter(
    (code) =>
      code.startsWith(`${prefix}-`) &&
      (countryCode === 'US' || INTERNATIONAL_SRP_PRODUCT_CODES.includes(code)),
  );
};

export const isSubscriptionSRPCapable = (
  subscription,
  isCobioticsSrpEnabled = false,
): boolean => {
  const planCode = getSubscriptionPlanCode(subscription);
  return (
    getSrpEligiblePlanCodes(isCobioticsSrpEnabled).includes(planCode) &&
    getSrpUpgradeCodes(
      planCode,
      getSubscriptionCountry(subscription),
      isCobioticsSrpEnabled,
    ).length > 0
  );
};

// Enrolled SRP-family subscriptions count in any country, flag or not, so members can still unenroll.
export const isSRPEnabledForSubscriptions = (
  subscriptions,
  isCobioticsSrpEnabled = false,
): boolean =>
  subscriptions &&
  subscriptions.filter(
    (sub) =>
      (isSubscriptionInSRP(sub) &&
        getSrpEligiblePlanCodes(true).includes(getSubscriptionPlanCode(sub))) ||
      isSubscriptionSRPCapable(sub, isCobioticsSrpEnabled),
  ).length > 0;

// Checks both shapes: raw API subscriptions use snake_case is_srp,
// normalized ones use isSrp.
export const isSubscriptionInSRP = (subscription): boolean =>
  subscription?.product_plan?.plan?.is_srp || subscription?.isSrp;

export const canSubscriptionEnrollInSRP = (
  subscription,
  isCobioticsSrpEnabled = false,
): boolean =>
  subscription.state?.toLowerCase() === 'active' &&
  isSubscriptionSRPCapable(subscription, isCobioticsSrpEnabled) &&
  !isSubscriptionInSRP(subscription);

export const getSRPProduct = (products) =>
  products.find((product) => product.code === SRP_PRODUCT_CODE);

export const getSRPProducts = (
  products: Product[],
  subscriptionPlanCode: string,
  countryCode: string,
  isCobioticsSrpEnabled = false,
): Product[] => {
  const upgradeCodes = getSrpUpgradeCodes(
    subscriptionPlanCode,
    countryCode,
    isCobioticsSrpEnabled,
  );
  return products
    .filter(
      (product) =>
        product.isSrp && upgradeCodes.includes(product.code.toLowerCase()),
    )
    .sort(
      (a, b) =>
        a.domesticAttributes.billingPeriodLength -
        b.domesticAttributes.billingPeriodLength,
    );
};

export type SrpTilePricing = {
  currencyCode: string;
  monthlyUnitPrice: number;
  monthlyDiscountUnitPrice: number;
  discountUnitPrice: number;
  discountAmount: number;
  discountPercent: number;
};

const roundToCent = (amount: number): number => Math.round(amount * 100) / 100;

// Prefers the plan's change preview (subscription's own currency); falls back to the
// always-USD plans-API attributes when there's no preview (e.g. reactivation).
export const getSrpTilePricing = (
  preview: SrpPlanPreview | undefined,
  attributes: ProductAttributes,
): SrpTilePricing => {
  const months = (attributes?.billingPeriodLength ?? 0) / 30;
  const unitAmount = Number(preview?.unitAmount);

  if (
    !preview ||
    !Number.isFinite(unitAmount) ||
    unitAmount <= 0 ||
    !(months > 0)
  ) {
    const hasAttributeDiscount =
      attributes?.monthlyUnitPrice !== attributes?.monthlyDiscountUnitPrice;
    return {
      currencyCode: 'USD',
      monthlyUnitPrice: attributes?.monthlyUnitPrice,
      monthlyDiscountUnitPrice: attributes?.monthlyDiscountUnitPrice,
      discountUnitPrice: attributes?.discountUnitPrice,
      discountAmount: hasAttributeDiscount ? attributes?.autoCouponAmount : 0,
      discountPercent: hasAttributeDiscount ? attributes?.autoCouponAmount : 0,
    };
  }

  // orderDiscount covers the whole order, so divide it back down to one unit.
  const quantity = Number(preview.quantity) > 0 ? Number(preview.quantity) : 1;
  const discountAmount = Math.min(
    Math.max(roundToCent(Number(preview.orderDiscount ?? 0) / quantity), 0),
    unitAmount,
  );
  const discountUnitPrice = roundToCent(unitAmount - discountAmount);

  return {
    currencyCode: preview.currency || 'USD',
    monthlyUnitPrice: roundToCent(unitAmount / months),
    monthlyDiscountUnitPrice: roundToCent(discountUnitPrice / months),
    discountUnitPrice,
    discountAmount,
    // From Shopify's amounts; a plan's plans-API coupon config can be missing.
    discountPercent: Math.round((discountAmount / unitAmount) * 100),
  };
};

export type SubscriptionChangeBody = {
  product_plan_id: number;
  timeframe: 'now';
  plan_code: string;
  coupon_codes: string[];
  price?: number;
  shipping: {
    amount: number;
    method_code: string;
  };
  quantity?: number;
  srp_now?: boolean;
};

export const createSubscriptionChangeBody = ({
  productId,
  productCode,
  couponCodes,
  address,
  srpNow = false,
  price = null,
  quantity = null,
}): SubscriptionChangeBody => {
  const isInternational = address.country !== 'US';
  const shippingAmount = getSrpProductCodes().includes(productCode)
    ? 0
    : isInternational
      ? 10
      : 0;
  const shippingMethod = shippingMethodCodeForCountry(address.country);
  const changeBody: SubscriptionChangeBody = {
    product_plan_id: productId,
    timeframe: 'now',
    plan_code: productCode,
    coupon_codes: couponCodes,
    shipping: {
      amount: shippingAmount,
      method_code: shippingMethod,
    },
  };
  if (srpNow) {
    changeBody.srp_now = srpNow;
  }
  if (quantity > 0) {
    changeBody.quantity = quantity;
  }
  if (price) {
    changeBody.price = price;
  }

  return changeBody;
};
